import React, { useState, useEffect } from 'react';
import { FiArrowRight, FiCheck, FiFileText, FiList, FiLink, FiUpload } from 'react-icons/fi';
import Footer from '../../components/Footer';

const APP_URL = 'https://wallet-king.app';
const ACCENT = '#00ff80';
const GLOW = 'rgba(0,255,128,0.12)';

const RAINBOW = [
  '#ff0040','#ff4000','#ff8000','#ffc000',
  '#ffff00','#80ff00','#00ff80','#00ffff',
  '#0080ff','#4000ff','#8000ff','#ff00ff',
];

function rainbowText(text) {
  const chars = text.split('');
  const vis = chars.filter(c => c !== ' ').length;
  let ci = 0;
  return chars.map((ch, i) => {
    if (ch === ' ') return <span key={i}>&nbsp;</span>;
    const color = RAINBOW[Math.floor((ci / vis) * RAINBOW.length) % RAINBOW.length];
    ci++;
    return <span key={i} style={{ color }}>{ch}</span>;
  });
}

function TypingDemo({ texts, interval = 4000 }) {
  const [idx, setIdx] = useState(0);
  const [shown, setShown] = useState(0);
  const [erasing, setErasing] = useState(false);

  useEffect(() => {
    const text = texts[idx];
    if (!erasing) {
      if (shown < text.length) {
        const t = setTimeout(() => setShown(s => s + 1), 30 + Math.random() * 25);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setErasing(true), interval);
      return () => clearTimeout(t);
    } else {
      if (shown > 0) {
        const t = setTimeout(() => setShown(s => s - 1), 15);
        return () => clearTimeout(t);
      }
      setErasing(false);
      setIdx(i => (i + 1) % texts.length);
    }
  }, [shown, erasing, idx, texts, interval]);

  return <span>{texts[idx].slice(0, shown)}<span className="pp2-cursor">|</span></span>;
}

const features = [
  { icon: <FiUpload />, title: 'Reads your statements', desc: 'Upload a PDF, CSV, or OFX bank statement. OCR pulls the transactions into your ledger. The free plan includes 3 statement uploads.', tag: 'OCR' },
  { icon: <FiList />, title: 'One ledger', desc: 'Income, expenses, merchants, and savings stay in one place instead of a spreadsheet you stop updating.', tag: 'LEDGER' },
  { icon: <FiLink />, title: 'Bank linking', desc: 'On the paid plan, connect a bank and keep the ledger current without uploading every statement by hand.', tag: 'BANK' },
  { icon: <FiFileText />, title: 'Built for personal budgets', desc: 'Wallet King tracks a personal budget. It is not a bank, a lender, or investment advice.', tag: 'YOU' },
];

const plans = [
  { name: 'Free', price: '$0', detail: '3 OCR statement uploads and the full ledger.' },
  { name: 'Paid', price: '$5/month', detail: 'More OCR uploads and bank linking.' },
];

const demoQueries = [
  'upload this month’s checking statement',
  'show spending by merchant',
  'how much did I save in September',
  'link my bank and pull new transactions',
];

export default function WalletKingPage() {
  return (
    <div className="product-page pp2-page" style={{ '--pp-accent': ACCENT, '--pp-glow': GLOW }}>
      <section className="pp2-hero">
        <div className="pp2-hero-inner">
          <div className="pp2-badge"><img src="/wallet-king-icon.svg" alt="" width="22" height="22" /> wallet-king.app</div>
          <h1 className="pp2-title">{rainbowText('Wallet King')}</h1>
          <p className="pp2-tagline-label">Personal budget tracker</p>
          <p className="pp2-subtitle">
            The personal budget tracker that reads your statements. Sign in with Google and keep one ledger for income, expenses, merchants, and savings.
          </p>
          <div className="pp2-beta-banner">
            <span className="pp2-beta-tag">LIVE</span>
            <span>Open at wallet-king.app — free to start, $5/month for bank linking</span>
          </div>
          <div className="pp2-actions">
            <a href={APP_URL} className="pp2-btn-primary">Open Wallet King <FiArrowRight /></a>
            <a href={`${APP_URL}/pricing`} className="pp2-btn-ghost">See pricing</a>
          </div>
        </div>
      </section>

      <section className="pp2-demo-section">
        <div className="pp2-demo-bar">
          <img src="/wallet-king-icon.svg" alt="" width="28" height="28" className="pp2-demo-icon" />
          <span className="pp2-demo-text">
            <TypingDemo texts={demoQueries} interval={3000} />
          </span>
        </div>
      </section>

      <section className="pp2-features">
        <div className="pp2-features-inner">
          <h2>A budget from the statements you already have.</h2>
          <p className="pp2-features-sub">Upload a statement, or link a bank on the paid plan.</p>
          <div className="pp2-features-grid">
            {features.map(f => (
              <div key={f.title} className="pp2-feature-card">
                <div className="pp2-feature-head">
                  <div className="pp2-feature-icon">{f.icon}</div>
                  <span className="pp2-feature-tag">{f.tag}</span>
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pp2-beta-section" id="open-app">
        <div className="pp2-beta-inner">
          <h2>Start on the free plan</h2>
          <p className="pp2-beta-sub">
            Wallet King is live at wallet-king.app. Three OCR statement uploads are free. Paid is $5 a month for more uploads and bank linking.
          </p>
          <div className="pp2-beta-perks">
            {plans.map(plan => (
              <div key={plan.name} className="pp2-beta-perk">
                <FiCheck /> <span><strong>{plan.name} · {plan.price}.</strong> {plan.detail}</span>
              </div>
            ))}
          </div>
          <a href={APP_URL} className="pp2-btn-primary">Open wallet-king.app <FiArrowRight /></a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
